import MUILink from '@mui/material/Link';
import React, { forwardRef, memo, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink, useNavigate } from 'react-router';

import routeConfiguration from '../../../routeConfiguration';
import { isIOS } from '../../../util/mobile/deviceDetection';
import { findRouteByRouteName, matchPathname, pathByRouteName } from '../../../util/routes';

/**
 * Link props as used by the Link component from MUI.
 * @param {object} props Props for the Link component. Accepts any additional properties.
 * @param {React.ReactNode} props.children The content of the link.
 * @param {string | object} props.to The destination of the link.
 * @param {boolean} [props.preload] Whether to preload the link.
 * @param {React.Ref<any>} ref Ref forwarded to the underlying element.
 * @returns {React.JSX.Element} The Link component.
 */
const LinkComponent = ({ children, to, preload = true, ...rest }, ref) => {
  const intl = useIntl();
  const navigate = useNavigate();

  // Memoize route configuration to avoid recalculating on each render
  const routes = useMemo(() => routeConfiguration(), []);

  // Process URL object once and memoize the result
  const { urlObject, routeComponent, routePathName, hasAnchor, url } = useMemo(() => {
    // Create URL object
    const urlObj = typeof to === 'object' ? { ...to } : { pathname: '' };

    // Process string URLs
    if (typeof to === 'string') {
      const parts = to.split('?');
      let pathname = parts[0];

      // Handle hash-only strings: extract hash and set pathname to current location
      if (pathname.startsWith('#')) {
        urlObj.hash = pathname.slice(1);
        urlObj.pathname = '';
      } else {
        // Normalize pathname
        if (!pathname.startsWith('/') && !pathname.startsWith('http')) {
          pathname = `/${pathname}`;
        }
        urlObj.pathname = pathname;
      }
      urlObj.search = parts[1] || '';
    }

    // Process route name and params
    if (typeof to === 'object' && to.name) {
      urlObj.name = to.name;
      urlObj.params = to.params;
      urlObj.search = typeof to.search === 'object' ? (new URLSearchParams(to.search)).toString() : to.search || '';
      urlObj.hash = to.hash || '';
      urlObj.state = to.state || {};
    }

    // External URLs must never go through React Router's <Link> — skip route matching
    const isExternal = urlObj.pathname?.startsWith('http');

    // Find route component only once
    const routeComp = isExternal
      ? null
      : (urlObj?.name && findRouteByRouteName(urlObj.name, routes)) ||
        (urlObj.pathname && matchPathname(urlObj.pathname, routes)[0]?.route);

    // Calculate path name
    const routePath =
      (urlObj.name && pathByRouteName(urlObj.name, routes, urlObj.params || {}, intl.locale)) ||
      urlObj.pathname;

    // Check for anchors
    const hasAnchorTag =
      (urlObj.pathname && urlObj.pathname.includes('#')) || urlObj.hash;

    // Create final URL for external links
    const finalUrl = urlObj.pathname + (urlObj.search ? `?${urlObj.search}` : '');

    return {
      urlObject: urlObj,
      routeComponent: routeComp,
      routePathName: routePath,
      hasAnchor: hasAnchorTag,
      url: finalUrl,
    };
  }, [to, routes, intl.locale]);

  // Optimize preload handler
  const handlePreload = useCallback(() => {
    if (routeComponent && preload && routeComponent.component) {
      const { component: Page } = routeComponent;
      if (Page && Page.preload) {
        Page.preload();
      }
    }
  }, [routeComponent, preload]);

  // Optimize anchor click handler
  const handleAnchorClick = useCallback(
    (event) => {
      event.preventDefault();
      if (!urlObject.pathname) return;

      const anchor = urlObject.pathname.split('#')[1];
      const element = document.getElementById(anchor);

      if (element) {
        navigate(routePathName, { replace: true });
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        navigate(routePathName);
      }
    },
    [urlObject.pathname, routePathName, navigate],
  );

  // Optimize iOS blank target handler
  const handleIOSBlankTarget = useCallback(
    (e) => {
      e.preventDefault();
      window.open(url, '_blank', 'location=no');
    },
    [url],
  );

  // Extract pathname + state — react-router v6/v7 expects `state` as a
  // sibling prop on <Link>, not inside the `to` object (where it's silently
  // ignored). Everything else (name, params, search, hash) still flows via
  // the spread into `to`.
  const { pathname, state, ...restUrlObject } = urlObject;

  // Render internal router link
  if (routeComponent) {
    return (
      <MUILink
        component={RouterLink}
        onMouseEnter={handlePreload}
        ref={ref}
        state={state}
        to={{ pathname: routePathName, ...restUrlObject }}
        {...(hasAnchor ? { onClick: handleAnchorClick } : {})}
        {...rest}
      >
        {children}
      </MUILink>
    );
  }

  // Render external link
  const isIOSandBlank = isIOS() && rest.target === '_blank';
  const clickHandler = isIOSandBlank ? { onClick: handleIOSBlankTarget } : {};

  return (
    <MUILink href={url} onMouseEnter={handlePreload} ref={ref} {...rest} {...clickHandler}>
      {children}
    </MUILink>
  );
};

LinkComponent.displayName = 'Link';

const ForwardedLink = forwardRef(LinkComponent);

// Use a custom comparison function to prevent unnecessary re-renders
const arePropsEqual = (prevProps, nextProps) => {
  // Compare essential props that would cause visual or behavioral changes
  return (
    prevProps.to === nextProps.to &&
    prevProps.preload === nextProps.preload &&
    prevProps.children === nextProps.children &&
    prevProps.className === nextProps.className &&
    prevProps.style === nextProps.style &&
    prevProps.target === nextProps.target
  );
};

const Link = memo(ForwardedLink, arePropsEqual);

export default Link;
